// Meta Pixel tracking layer for dtech.rs
// Wraps window.fbq with safe, typed helpers used across the app.
// Pixel ID: 2945020945847469

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
  }
}

export const META_PIXEL_ID = '2945020945847469'

function fbqReady(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function'
}

/** Mirror any event to GA4 (gtag) so both platforms stay in sync. */
function trackGA(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag
  if (typeof gtag !== 'function') return
  gtag('event', event, params || {})
}

/**
 * Standard Meta Pixel event (PageView, Lead, Contact, ViewContent, etc.),
 * mirrored to GA4. Each call fires exactly once per platform — no duplicates.
 */
export function trackPixel(
  event: string,
  params?: Record<string, unknown>,
): void {
  const enriched = withPageInfo(params || {})
  if (fbqReady()) window.fbq!('track', event, enriched)
  trackGA(event, enriched)
}

/**
 * Custom Meta Pixel event (HighIntentVisitor, ScrollDepth, etc.), mirrored to
 * GA4.
 */
export function trackPixelCustom(
  event: string,
  params?: Record<string, unknown>,
): void {
  const enriched = withPageInfo(params || {})
  if (fbqReady()) window.fbq!('trackCustom', event, enriched)
  trackGA(event, enriched)
}

/**
 * Advanced Matching — re-initializes the pixel with hashed user data so Meta
 * can match conversions to accounts. Meta hashes these client-side. Only call
 * with data the user explicitly submitted (form fields).
 */
export function setAdvancedMatching(data: {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
}): void {
  if (!fbqReady()) return
  const payload: Record<string, string> = {}
  if (data.email) payload.em = data.email.trim().toLowerCase()
  if (data.phone) payload.ph = data.phone.replace(/[^\d]/g, '')
  if (data.firstName) payload.fn = data.firstName.trim().toLowerCase()
  if (data.lastName) payload.ln = data.lastName.trim().toLowerCase()
  if (Object.keys(payload).length === 0) return
  window.fbq!('init', META_PIXEL_ID, payload)
}

/** Common page context attached to every event for richer audiences. */
export function getPageInfo(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const w = window.innerWidth
  const device_type = w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop'
  return {
    page_title: document.title,
    page_url: window.location.href,
    page_path: window.location.pathname,
    referrer: document.referrer || 'direct',
    device_type,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
  }
}

function withPageInfo(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return { ...getPageInfo(), ...params }
}

// ---- Convenience helpers for common interactions -------------------------

export function trackPhoneClick(buttonText?: string): void {
  trackPixel('Contact', { contact_method: 'phone', button_text: buttonText })
  trackPixelCustom('PhoneClick', { button_text: buttonText })
}

export function trackEmailClick(buttonText?: string): void {
  trackPixel('Contact', { contact_method: 'email', button_text: buttonText })
  trackPixelCustom('EmailClick', { button_text: buttonText })
}

export function trackWhatsAppClick(buttonText?: string): void {
  trackPixel('Contact', {
    contact_method: 'whatsapp',
    button_text: buttonText || 'WhatsApp',
  })
  trackPixelCustom('WhatsAppClick', { button_text: buttonText || 'WhatsApp' })
}

export function trackExternalLink(
  url: string,
  linkType?: 'external' | 'manufacturer' | 'partner',
  buttonText?: string,
): void {
  trackPixelCustom('ExternalLinkClick', {
    url,
    link_type: linkType || 'external',
    button_text: buttonText,
  })
}

/** Generic file download (PDF, spec sheets, etc.). */
export function trackFileDownload(url: string, buttonText?: string): void {
  trackPixelCustom('FileDownload', { url, button_text: buttonText })
}

/** Catalog / brochure download. */
export function trackDownloadCatalog(url: string, buttonText?: string): void {
  trackPixelCustom('DownloadCatalog', { url, button_text: buttonText })
}

export function trackQuoteClick(
  buttonText?: string,
  productName?: string,
): void {
  trackPixelCustom('QuoteRequestClick', {
    button_text: buttonText || 'Zatrazite ponudu',
    product_name: productName,
  })
}

export function trackProductInquiryClick(productName?: string): void {
  trackPixelCustom('ProductInquiryClick', { product_name: productName })
}

// ---- Form lifecycle ------------------------------------------------------

export function trackFormOpened(formName: string): void {
  trackPixelCustom('FormOpened', { form_name: formName })
}

export function trackFormStarted(formName: string): void {
  trackPixelCustom('FormStarted', { form_name: formName })
}

export function trackFormCompleted(
  formName: string,
  params?: Record<string, unknown>,
): void {
  trackPixelCustom('FormCompleted', { form_name: formName, ...params })
}

export function trackFormAbandoned(formName: string): void {
  trackPixelCustom('FormAbandoned', { form_name: formName })
}

// ---- Product-level tracking ---------------------------------------------

type ProductCtx = {
  product_name?: string
  product_category?: string
}

export function trackProductViewed(ctx: ProductCtx): void {
  trackPixel('ViewContent', {
    content_type: 'product',
    content_name: ctx.product_name,
    ...ctx,
  })
  trackPixelCustom('ProductViewed', ctx)
}

export function trackProductSpecsViewed(ctx: ProductCtx): void {
  trackPixelCustom('ProductSpecsViewed', ctx)
}

export function trackProductGalleryViewed(ctx: ProductCtx): void {
  trackPixelCustom('ProductGalleryViewed', ctx)
}

export function trackInquiryStarted(ctx: ProductCtx): void {
  trackPixel('InitiateCheckout', {
    content_type: 'product',
    content_name: ctx.product_name,
    ...ctx,
  })
  trackPixelCustom('InquiryStarted', ctx)
}

export function trackInquirySubmitted(ctx: ProductCtx): void {
  trackPixelCustom('InquirySubmitted', ctx)
}

// ---- Scroll depth + engagement custom events -----------------------------

export function trackScrollDepth(depth: 25 | 50 | 75 | 90): void {
  trackPixelCustom('ScrollDepth', { depth: `${depth}%`, depth_value: depth })
}

export function trackHighIntentVisitor(reason: string): void {
  trackPixelCustom('HighIntentVisitor', { trigger: reason })
}

export function trackEngagedVisitor(reason: string): void {
  trackPixelCustom('EngagedVisitor', { trigger: reason })
}
